import { FC, Fragment, useMemo } from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { ALL_PERMISSIONS, Permission, PERMISSION_META } from "constants/permissions"

import { RoleRow } from "services/role-management/role-management.service"

type GroupName = "Pages" | "Actions"
const GROUPS: GroupName[] = ["Pages", "Actions"]

const formatBuiltinName = (id: string) =>
  id
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase())

const cellKey = (roleId: string, perm: Permission) => `${roleId}:${perm}`

const cellBorderClass = "border-gray-200 dark:border-[rgba(255,255,255,0.08)]"
const subtleTextClass = "text-[rgba(93,96,101,1)] dark:text-neutral-400"

interface Props {
  roles: RoleRow[]
  /** Set of `${roleId}:${permission}` keys currently saving — disables the matching checkbox. */
  pending: Set<string>
  /** Called when a cell checkbox toggles. `currentlyHas` is the cell's state BEFORE the click. */
  onToggle: (roleId: string, perm: Permission, currentlyHas: boolean) => void
  /** Called when the X icon on a custom role's column header is clicked. */
  onDeleteRole: (role: RoleRow) => void
}

export const RolePermissionMatrix: FC<Props> = ({ roles, pending, onToggle, onDeleteRole }) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const groupedPermissions = useMemo<Record<GroupName, Permission[]>>(() => {
    const out: Record<GroupName, Permission[]> = { Pages: [], Actions: [] }
    for (const p of ALL_PERMISSIONS) {
      out[PERMISSION_META[p].group].push(p)
    }
    return out
  }, [])

  const groupLabel = (group: GroupName) => (group === "Pages" ? t("roleManagement.groupPages") : t("roleManagement.groupActions"))

  return (
    <div className={clsx("rounded-lg border overflow-x-auto bg-white dark:bg-[#0C0C1A]", cellBorderClass)}>
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-white text-[rgba(6,7,21,1)] dark:bg-[#131421] dark:text-white">
          <tr>
            <th className={clsx("text-left px-4 py-3 border-b min-w-[280px]", cellBorderClass)}>{t("roleManagement.permission")}</th>
            {roles.map((r) => (
              <th key={r.id} className={clsx("px-3 py-3 border-b text-center font-medium whitespace-nowrap", cellBorderClass)}>
                <div className="flex items-center justify-center gap-2">
                  <span>{r.is_builtin ? formatBuiltinName(r.id) : r.name}</span>
                  {!r.is_builtin && (
                    <button
                      title={
                        r.user_count > 0
                          ? (t("roleManagement.usersAssignedTooltip", { count: r.user_count }) as string)
                          : (t("roleManagement.deleteRole") as string)
                      }
                      onClick={() => onDeleteRole(r)}
                      className={clsx(
                        "text-xs leading-none px-1.5 py-0.5 rounded",
                        r.user_count > 0 ? "text-neutral-500 cursor-not-allowed" : "text-red-500 hover:bg-red-500/10 cursor-pointer"
                      )}
                    >
                      ✕
                    </button>
                  )}
                </div>
                <div className={clsx("text-[10px] font-normal mt-0.5", subtleTextClass)}>
                  {r.is_builtin ? t("roleManagement.builtin") : t("roleManagement.customWithUsers", { count: r.user_count })}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {GROUPS.map((group) => (
            <Fragment key={group}>
              <tr>
                <td
                  colSpan={1 + roles.length}
                  className={clsx(
                    "px-4 py-2 text-xs uppercase tracking-wide font-semibold border-b",
                    "bg-gray-50 text-[rgba(93,96,101,1)] dark:bg-[#131421] dark:text-neutral-300",
                    cellBorderClass
                  )}
                >
                  {groupLabel(group)}
                </td>
              </tr>
              {groupedPermissions[group].map((perm) => (
                <tr key={perm} className="hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.02)]">
                  <td className={clsx("px-4 py-2 border-b align-top", cellBorderClass)}>
                    <div className="font-medium text-[rgba(6,7,21,1)] dark:text-white">{PERMISSION_META[perm].label}</div>
                    <div className={clsx("text-xs mt-0.5", subtleTextClass)}>{perm}</div>
                  </td>
                  {roles.map((role) => {
                    const has = role.permissions.includes(perm)
                    const key = cellKey(role.id, perm)
                    const inFlight = pending.has(key)
                    return (
                      <td key={role.id} className={clsx("px-3 py-2 border-b text-center", cellBorderClass)}>
                        <input
                          type="checkbox"
                          checked={has}
                          disabled={inFlight}
                          onChange={() => onToggle(role.id, perm, has)}
                          className={clsx("w-5 h-5 align-middle cursor-pointer", inFlight && "opacity-40 cursor-wait")}
                          aria-label={`${perm} for ${role.id}`}
                        />
                      </td>
                    )
                  })}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}
